/**
 * State
 */
const state = {
    allowedCarriers: [
        'ee',
        'o2',
        'samsungrepair',
        'samsungrepair-de',
        'tesco',
        'three',
        'vodafone',
        'wefix',
        'wefix-ca',
        'likewize',
        'likewize-au',
        'likewize-us'
    ],
    carriersWithInsuranceJourney: [
        // 'o2'
    ],
    carriersWithSingleDeviceBooking: [
        'tesco'
    ],
    carriersWithExternalRedirectOnReferenceFailure: [
        'tesco'
    ],

    allowsMultiDeviceBooking: true,
    allowsInsuranceOrder: false,
    allowsStoreDropOff: false,
    allowsInStoreBooking: false,
    redirectOnMissingReference: false,
    carrier: null,
    carrierDetails: {},
    isBusinessAccount: false,
    repairType: 'mobile',
    store: null,
    storesWithDropOffEnabled: [],
    ignoreStore: false,
    referrer: null,
    channel: null,
    lucidRef: null
};

/**
 * Getters
 */
const getters = {
    /** Returns a bool of if we're on a carrier journey that allows an insurance journey */
    allowsInsuranceOrder: (state) => state.allowsInsuranceOrder,

    /** Returns a bool of if we're on a carrier journey that allows a multi device booking */
    allowsMultiDeviceBooking: (state) => state.allowsMultiDeviceBooking,

    /** Returns a bool of if we're on a carrier journey that allows a drop off of device at the current store */
    allowsStoreDropOff: (state) => state.allowsStoreDropOff,

    /** Returns a bool of if we're on a carrier journey that allows an in store booking */
    allowsInStoreBooking: (state) => state.allowsInStoreBooking,

    /** Returns a boolean to determine whether or not to redirect to an external url if a reference is missing */
    redirectOnMissingReference: (state) => state.redirectOnMissingReference,

    /** Returns the carrier that we're booking under, or null for WeFix */
    carrier: (state) => state.carrier,

    /** The store, if we're at one */
    store: (state) => state.store,

    /** The store code, if we have a store **/
    storeCode: (state) => (state.store && state.store.code) ? state.store.code : null,

    /** Current store allows in store drop off */
    currentStoreAllowsInStoreDropOff: (state) => (state.store) ? state.store.drop_off_available : false,

    /** Cache of the stores that have drop off enabled */
    storesWithDropOffEnabled: (state) => state.storesWithDropOffEnabled,

    /** Returns a bool of if we need to enter the store code */
    requiresStore: (state) => (state.carrier === 'vodafone' && !state.store && !state.ignoreStore),

    /** Return details about the carrier */
    carrierDetails: (state) => state.carrierDetails,

    isBookingInStore: (state) => (state.store !== null),

    isBusinessAccount: (state) => (state.isBusinessAccount),

    repairType: (state) => (state.repairType),

    onlyOfferSamsung: (state) => (state.carrier === 'ee' || state.carrier === 'three' || state.carrier === 'samsungrepair' || state.carrier === 'samsungrepair-de' || state.referrer === 'samsung'),

    isApplianceRepair: (state) => state.carrier === 'appliance',

    referrer: (state) => state.referrer,

    channel: (state) => state.channel,

    lucidRef: (state) => state.lucidRef
};

/**
 * Actions
 */
const actions = {
    /** Captures the carrier we're booking under and if they allow insurance bookings */
    setCarrier({state, commit}) {
        return new Promise((resolve) => {
            let carrier = null;
            if (state.allowedCarriers.includes(process.env.VUE_APP_PARTNER_ENVIRONMENT)) {
                carrier = process.env.VUE_APP_PARTNER_ENVIRONMENT;
            }

            commit('SET_CARRIER', carrier);

            if (carrier && state.carriersWithInsuranceJourney.includes(carrier)) {
                commit('SET_ALLOW_INSURANCE_ORDER', true);
            } else {
                commit('SET_ALLOW_INSURANCE_ORDER', false);
            }

            if (carrier && state.carriersWithSingleDeviceBooking.includes(carrier)) {
                commit('SET_ALLOW_MULTI_DEVICE_BOOKING', false);
            } else {
                commit('SET_ALLOW_MULTI_DEVICE_BOOKING', true);
            }

            const searchParams = new URLSearchParams(window.location.search);
            const hashParams = new URLSearchParams(window.location.hash.slice(1));

            const referrer = searchParams.get('referrer') || hashParams.get('referrer');
            if (referrer) commit('SET_REFERRER', referrer);

            const channel = searchParams.get('channel') || hashParams.get('channel');
            if (channel) commit('SET_CHANNEL', channel);

            const lucidRef = searchParams.get('lucidref') || hashParams.get('lucidref');
            if (lucidRef) commit('SET_LUCIDREF', lucidRef);

            let carrierData = {};
            switch (carrier) {
                case "samsungrepair": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: true,
                        email: "contact@samsungrepair.com",
                        externalRedirectUrl: "",
                        formattedName: "Samsung Repair",
                        formattedWeb: "samsungrepair.com",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "samsung-colour",
                        name: "samsung",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://samsungrepair.com",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "samsungrepair-de": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services Limited, Ratinger Str. 10, 42579 Heiligenhaus, Germany",
                        hasCallOutCharge: true,
                        callOutCharge: "99",
                        allowWarrantyRepairs: true,
                        email: "contact@likewizerepair.de",
                        externalRedirectUrl: "",
                        formattedName: "Samsung Repair",
                        formattedWeb: "samsung-repair.de",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "samsung-colour",
                        name: "samsung",
                        phone: "-",
                        privacyLink: "https://likewize-files.s3.eu-west-2.amazonaws.com/de/documents/Likewize_Repair_Datenschutzhinweise_fu%CC%88r_Kunden.pdf",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://samsung-repair.de",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: false,
                        countryCodeAlpha2: "DE",
                        currency: "EUR",
                        use3DSecure: true,
                    }
                    break;
                }
                case "likewize": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: false,
                        email: "repaircontact@likewize.com",
                        externalRedirectUrl: "",
                        formattedName: "Likewize",
                        formattedWeb: "likewizerepair.co.uk",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "likewize-logo-pink",
                        name: "likewize",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://likewizerepair.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/likewize/apple-touch-icon.png",
                            "icon-16": "img/icons/likewize/favicon-16x16.png",
                            "icon-32": "img/icons/likewize/favicon-32x32.png",
                            "icon-manifest": "img/icons/likewize/site.webmanifest",
                            "mask-icon": "img/icons/likewize/safari-pinned-tab.svg",
                            "favicon": "img/icons/likewize/favicon.ico",
                            "meta-msapplication-tile-color": "#3200be",
                            "meta-theme-color": "#ff96ff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "likewize-au": {
                    carrierData = {
                        redirect: null,
                        isInternational: true,
                        address: "Likewize Services Limited, Level 9, South Tower, 485 La Trobe Street, Melbourne, Victoria, Australia 3000",
                        hasCallOutCharge: true,
                        callOutCharge: "99.00",
                        allowWarrantyRepairs: false,
                        email: "contact@likewizerepair.com.au",
                        externalRedirectUrl: "",
                        formattedName: "Likewize",
                        formattedWeb: "likewizerepair.com.au",
                        liveChat: false,
                        liveChatLicence: null,
                        logoPath: "likewize-logo-pink",
                        name: "likewize",
                        phone: "1800 568 979",
                        privacyLink: "",
                        registrationDetails: "",
                        termsLink: "",
                        web: "https://likewizerepair.com.au",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: false,
                        icons: {
                            "apple-touch-icon": "img/icons/likewize/apple-touch-icon.png",
                            "icon-16": "img/icons/likewize/favicon-16x16.png",
                            "icon-32": "img/icons/likewize/favicon-32x32.png",
                            "icon-manifest": "img/icons/likewize/site.webmanifest",
                            "mask-icon": "img/icons/likewize/safari-pinned-tab.svg",
                            "favicon": "img/icons/likewize/favicon.ico",
                            "meta-msapplication-tile-color": "#3200be",
                            "meta-theme-color": "#ff96ff"
                        },
                        countryCodeAlpha2: "AU",
                        currency: "AUD",
                        use3DSecure: false,
                    }
                    break;
                }
                case "likewize-us": {
                    carrierData = {
                        redirect: null,
                        isInternational: true,
                        address: "Likewize Services Limited, 1900 W Kirkwood Blvd, Suite 1600C, Southlake, TX 76092",
                        hasCallOutCharge: true,
                        callOutCharge: "99.00",
                        allowWarrantyRepairs: false,
                        email: "LikewizeRepair@likewize.com",
                        externalRedirectUrl: "",
                        formattedName: "Likewize Repair",
                        formattedWeb: "likewizerepair.com",
                        liveChat: false,
                        liveChatLicence: null,
                        logoPath: "likewize-logo-pink",
                        name: "likewize",
                        phone: "1-800-930-0159",
                        privacyLink: "https://likewize.com/privacy-notice-usa",
                        registrationDetails: "",
                        termsLink: "",
                        web: "https://likewizerepair.com",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: false,
                        icons: {
                            "apple-touch-icon": "img/icons/likewize/apple-touch-icon.png",
                            "icon-16": "img/icons/likewize/favicon-16x16.png",
                            "icon-32": "img/icons/likewize/favicon-32x32.png",
                            "icon-manifest": "img/icons/likewize/site.webmanifest",
                            "mask-icon": "img/icons/likewize/safari-pinned-tab.svg",
                            "favicon": "img/icons/likewize/favicon.ico",
                            "meta-msapplication-tile-color": "#3200be",
                            "meta-theme-color": "#ff96ff"
                        },
                        countryCodeAlpha2: "US",
                        currency: "USD",
                        use3DSecure: false,
                    }
                    break;
                }
                case "ee": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: true,
                        email: "repaircontact@likewize.com",
                        formattedName: "EE",
                        formattedWeb: "likewizerepair.co.uk",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "ee-colour",
                        name: "ee",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Company registration no: 08401611",
                        termsLink: "",
                        web: "https://likewizerepair.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/ee/apple-touch-icon.png",
                            "icon-16": "img/icons/ee/favicon-16x16.png",
                            "icon-32": "img/icons/ee/favicon-32x32.png",
                            "icon-manifest": "img/icons/ee/site.webmanifest",
                            "mask-icon": "img/icons/ee/safari-pinned-tab.svg",
                            "favicon": "img/icons/ee/favicon.ico",
                            "meta-msapplication-tile-color": "#009c9c",
                            "meta-theme-color": "#ffffff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "o2": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: true,
                        email: "repaircontact@likewize.com",
                        formattedName: "O2 Repair",
                        formattedWeb: "likewizerepair.co.uk",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "o2-white",
                        name: "o2",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://likewizerepair.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/o2repair/apple-touch-icon.png",
                            "icon-16": "img/icons/o2repair/favicon-16x16.png",
                            "icon-32": "img/icons/o2repair/favicon-32x32.png",
                            "icon-manifest": "img/icons/o2repair/site.webmanifest",
                            "mask-icon": "img/icons/o2repair/safari-pinned-tab.svg",
                            "favicon": "img/icons/o2repair/favicon.ico",
                            "meta-msapplication-tile-color": "#0019a5",
                            "meta-theme-color": "#ffffff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "tesco": {
                    carrierData = {
                        redirect: "",
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: true,
                        email: "repaircontact@likewize.com",
                        externalRedirectUrl: "https://support.tescomobile.com/customer/picker.html",
                        externalRedirectHeader: "Thank you for visiting our Tesco Mobile Repair site",
                        externalRedirectMessage: "If you're having problems with your phone, take a look at our <a href=\"https://support.tescomobile.com/customer/picker.html\">Device Help</a> for support and information.",
                        formattedName: "Tesco Mobile",
                        formattedWeb: "likewizerepair.co.uk",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "tesco-mobile-colour",
                        name: "tesco-mobile",
                        phone: "023 8214 9045",
                        privacyLink: "",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://likewizerepair.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/tesco/apple-touch-icon.png",
                            "icon-16": "img/icons/tesco/favicon-16x16.png",
                            "icon-32": "img/icons/tesco/favicon-32x32.png",
                            "icon-manifest": "img/icons/tesco/site.webmanifest",
                            "mask-icon": "img/icons/tesco/safari-pinned-tab.svg",
                            "favicon": "img/icons/tesco/favicon.ico",
                            "meta-msapplication-tile-color": "#01539f",
                            "meta-theme-color": "#ffffff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "three": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: true,
                        email: "repaircontact@likewize.com",
                        formattedName: "Three Repair",
                        formattedWeb: "likewizerepair.co.uk",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "three-powered-by-likewize-logo",
                        name: "three",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://likewizerepair.co.uk",
                        additionalLandingPage: true,
                        additionalLandingPageLimit: 3,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/three/apple-touch-icon.png",
                            "icon-16": "img/icons/three/favicon-16x16.png",
                            "icon-32": "img/icons/three/favicon-32x32.png",
                            "icon-manifest": "img/icons/three/site.webmanifest",
                            "mask-icon": "img/icons/three/safari-pinned-tab.svg",
                            "favicon": "img/icons/three/favicon.ico",
                            "meta-msapplication-tile-color": "#6D22E9",
                            "meta-theme-color": "#ffffff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "vodafone": {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Vodafone Limited. Registered office: Vodafone House, The Connection, Newbury, Berkshire RG14 2FN",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: true,
                        email: "repaircontact@likewize.com",
                        externalRedirectUrl: "",
                        formattedName: "Vodafone Repair",
                        formattedWeb: "repair.vodafone.co.uk",
                        landingPageLogoPath: "vodafone-min-colour",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "vodafone-min-white",
                        name: "vodafone",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Registered in England No 1471587",
                        termsLink: "",
                        web: "https://repair.vodafone.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/vodafone/apple-touch-icon.png",
                            "icon-16": "img/icons/vodafone/favicon-16x16.png",
                            "icon-32": "img/icons/vodafone/favicon-32x32.png",
                            "icon-manifest": "img/icons/vodafone/site.webmanifest",
                            "mask-icon": "img/icons/vodafone/safari-pinned-tab.svg",
                            "favicon": "img/icons/vodafone/favicon.ico",
                            "meta-msapplication-tile-color": "#e40100",
                            "meta-theme-color": "#ffffff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                    break;
                }
                case "wefix-ca": {
                    carrierData = {
                        redirect: null,
                        isInternational: true,
                        address: "CANADA ADDRESS REQUIRED.",
                        hasCallOutCharge: true,
                        callOutCharge: "99.00",
                        allowWarrantyRepairs: false,
                        email: "repaircontact@likewize.com",
                        externalRedirectUrl: "",
                        formattedName: "WeFix",
                        formattedWeb: "canadaweb.wefix.co.uk",
                        liveChat: false,
                        liveChatLicence: null,
                        logoPath: "wefix-colour",
                        name: "wefix",
                        phone: "CANADA PHONE REQUIRED",
                        privacyLink: "",
                        registrationDetails: "",
                        termsLink: "",
                        web: "https://canadaweb.wefix.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: false,
                        icons: {
                            "apple-touch-icon": "img/icons/wefix/apple-touch-icon.png",
                            "icon-16": "img/icons/wefix/favicon-16x16.png",
                            "icon-32": "img/icons/wefix/favicon-32x32.png",
                            "icon-manifest": "img/icons/wefix/site.webmanifest",
                            "mask-icon": "img/icons/wefix/safari-pinned-tab.svg",
                            "favicon": "img/icons/wefix/favicon.ico",
                            "meta-msapplication-tile-color": "#0c2340",
                            "meta-theme-color": "#ffffff"
                        },
                        countryCodeAlpha2: "CA",
                        currency: "CAD",
                        use3DSecure: false,
                    }
                    break;
                }
                default: {
                    carrierData = {
                        redirect: null,
                        isInternational: false,
                        address: "Likewize Services UK Limited, Jack Mills Way, Shavington, Crewe, Cheshire, CW2 5XF",
                        hasCallOutCharge: true,
                        callOutCharge: "59.00",
                        allowWarrantyRepairs: false,
                        email: "repaircontact@likewize.com",
                        externalRedirectUrl: "",
                        formattedName: "Likewize Repair",
                        formattedWeb: "likewizerepair.co.uk",
                        liveChat: true,
                        liveChatLicence: 12034224,
                        logoPath: "likewize-logo-pink",
                        name: "likewize",
                        phone: "0808 164 0557",
                        privacyLink: "",
                        registrationDetails: "Company Registration No: 08401611",
                        termsLink: "",
                        web: "https://likewizerepair.co.uk",
                        additionalLandingPage: false,
                        additionalLandingPageLimit: 7,
                        usesKlarna: true,
                        icons: {
                            "apple-touch-icon": "img/icons/likewize/apple-touch-icon.png",
                            "icon-16": "img/icons/likewize/favicon-16x16.png",
                            "icon-32": "img/icons/likewize/favicon-32x32.png",
                            "icon-manifest": "img/icons/likewize/site.webmanifest",
                            "mask-icon": "img/icons/likewize/safari-pinned-tab.svg",
                            "favicon": "img/icons/likewize/favicon.ico",
                            "meta-msapplication-tile-color": "#3200be",
                            "meta-theme-color": "#ff96ff"
                        },
                        countryCodeAlpha2: "GB",
                        currency: "GBP",
                        use3DSecure: true,
                    }
                }
            }
            this.$axios.get('/api/v4/wefix-booking/carrier/' + carrier)
                .then((response) => {
                    Object.keys(response.data.data.settings).forEach((key) => {
                        if (response.data.data.settings[key] !== undefined
                            && response.data.data.settings[key] !== null
                            && response.data.data.settings[key].length > 0) {
                            carrierData[key] = response.data.data.settings[key];
                        }
                    });
                    commit('SET_CARRIER_DETAILS', carrierData);
                    commit('SET_ALLOW_IN_STORE_BOOKING', response.data.data.allows_in_store_bookings);
                    commit('SET_ALLOW_STORE_DROP_OFF', response.data.data.allows_in_store_drop_off);
                }).catch(error => {
                // We need to set this here as well, so that if the API fails we still set the correct details.
                commit('SET_CARRIER_DETAILS', carrierData);
                console.log(error)
            });

            if (carrier && state.carriersWithExternalRedirectOnReferenceFailure.includes(carrier)) {
                commit('SET_REDIRECT_BROWSER_ON_MISSING_TOKEN', true);
            } else {
                commit('SET_REDIRECT_BROWSER_ON_MISSING_TOKEN', false);
            }
            resolve();
        });
    },

    /** Captures the store, if one has been supplied */
    setStore({commit, getters, state}, storeCode) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/${getters.reference}/store`, {
                carrier: state.carrier,
                store_code: storeCode
            }).then((response) => {
                commit('SET_STORE', response.data.data);
                resolve();
            }).catch(error => {
                reject(error);
            });
        });
    },

    /** Sets the ignore store field so the user doesn't see the store entry page */
    setIgnoreStore({commit}) {
        return new Promise((resolve) => {
            commit('SET_IGNORE_STORE', true);
            resolve();
        });
    },

    /** Reset the store to null and ignore state to false */
    resetStoreSettings({commit}) {
        return new Promise((resolve) => {
            commit('SET_STORE', null);
            commit('SET_IGNORE_STORE', false);
            resolve();
        });
    },

    /** Sets if this is a business booking **/
    setBusinessAccount({commit}, isBusinessAccount) {
        return new Promise((resolve) => {
            commit('SET_BUSINESS_ACCOUNT', isBusinessAccount);
            resolve();
        });
    },

    /** Sets if this is a business booking **/
    setRepairType({commit}, repairType) {
        return new Promise((resolve) => {
            commit('SET_REPAIR_TYPE', repairType);
            resolve();
        });
    },

    /** Fetch closest drop off enabled stores */
    getClosestDropOffEnabledStores({commit}, {postcode, carrier}) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/company/stores/locate/${postcode}/${carrier}`).then((response) => {
                commit('SET_STORES_WITH_DROP_OFF_ENABLED', response.data);
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /** Get the distance from the provided postcode to the provided store */
    getDistanceToStore(_, postcode) {
        return new Promise((resolve, reject) => {
            this.$axios.get(`/api/v4/company/stores/locate/${postcode}/${state.carrier}`).then((response) => {
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /** Fetch closest drop off enabled stores */
    getStoreData({state}, {storeId, postcode}) {
        return new Promise((resolve, reject) => {
            this.$axios.post(`/api/v4/wefix-booking/carrier/${state.carrier}/store/${storeId}`, {
                postcode: postcode
            }).then((response) => {
                resolve(response);
            }).catch(error => {
                reject(error);
            });
        });
    },

    /**
     * Clear the third party referrer
     * @param commit
     */
    clearReferrer({commit}) {
        return new Promise((resolve) => {
            commit('CLEAR_REFERRER');
            commit('CLEAR_CHANNEL');
            commit('CLEAR_LUCIDREF');
            resolve();
        });
    }
};

/**
 * Mutations
 */
const mutations = {
    /** Set the allow insurance order check */
    SET_ALLOW_INSURANCE_ORDER(state, allow) {
        state.allowsInsuranceOrder = allow;
    },

    /** Set the allow multi device booking check */
    SET_ALLOW_MULTI_DEVICE_BOOKING(state, allow) {
        state.allowsMultiDeviceBooking = allow;
    },

    /** Redirect the user to an external url if booking reference is missing (tesco, samsung, etc.) */
    SET_REDIRECT_BROWSER_ON_MISSING_TOKEN(state, redirect) {
        state.redirectOnMissingReference = redirect;
    },

    /** Set the allow drop off check check */
    SET_ALLOW_STORE_DROP_OFF(state, allow) {
        state.allowsStoreDropOff = allow;
    },

    /** Set the allow drop off check check */
    SET_ALLOW_IN_STORE_BOOKING(state, allow) {
        state.allowsInStoreBooking = allow;
    },

    /** Set the carrier we're booking under */
    SET_CARRIER(state, carrier) {
        state.carrier = carrier;
    },

    SET_CARRIER_DETAILS(state, data) {
        state.carrierDetails = data;
    },

    /** Set if this is a business booking **/
    SET_BUSINESS_ACCOUNT(state, isBusinessAccount) {
        state.isBusinessAccount = isBusinessAccount;
    },

    /** Set if this is a business booking **/
    SET_REPAIR_TYPE(state, repairType) {
        state.repairType = repairType;
    },

    /** Set the store that we're at */
    SET_STORE(state, store) {
        state.store = store;
    },

    /** Set the store that we're at */
    SET_STORES_WITH_DROP_OFF_ENABLED(state, stores) {
        state.storesWithDropOffEnabled = stores;
    },

    /** Set the ignore store state */
    SET_IGNORE_STORE(state, ignore) {
        state.ignoreStore = ignore;
        state.store = null;
    },

    /** Set the third party referrer */
    SET_REFERRER(state, referrer) {
        state.referrer = referrer;
    },

    /** Clear the third party referrer */
    CLEAR_REFERRER(state) {
        state.referrer = null;
    },

    /** Set the third party referrer channel */
    SET_CHANNEL(state, channel) {
        state.channel = channel;
    },

    /** Clear the third party referrer channel */
    CLEAR_CHANNEL(state) {
        state.channel = null;
    },

    /** Set the Samsung lucidref */
    SET_LUCIDREF(state, lucidRef) {
        state.lucidRef = lucidRef;
    },

    /** Clear the Samsung lucidref */
    CLEAR_LUCIDREF(state) {
        state.lucidRef = null;
    }
};

export default {
    state,
    getters,
    actions,
    mutations
}
